import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useExperimentContext } from '@/experimentation'
import { isCookieConsentDialogVisible } from '@/molecules/CookieManagement'
import { AppBannerAnalyticsContext } from '@/organisms/GetAppBanner/types'
import { getBooleanFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import { AppPromoModal } from '../AppPromoModal'
import { UPDATED_LEGAL_DOCS_MODAL_ID } from '../UpdatedLegalDocsModal/UpdatedLegalDocsModal'

export interface GetAppBannerContext extends PropsWithChildren<unknown> {
  deepLink: string | null
  bannerVisible: boolean
  isLoading: boolean
}

export type GetAppBannerProviderProps = PropsWithChildren<{
  deepLink: string | null
  projectSlug?: string
  context: AppBannerAnalyticsContext
}>

export type GetAppBannerProvider = (props: GetAppBannerProviderProps) => ReactElement<GetAppBannerContext>

export const GetAppBannerContext = createContext<GetAppBannerContext>({
  deepLink: null,
  bannerVisible: false,
  isLoading: true,
})

export const GetAppBannerProvider = ({
  children,
  deepLink,
  projectSlug,
  context,
}: GetAppBannerProviderProps): ReactElement<GetAppBannerContext> => {
  const [bannerVisible, setBannerVisible] = useState(false)
  const { getFeatureValue } = useExperimentContext()

  useEffect(() => {
    if (!isMobile) return

    const timeout = setTimeout(() => {
      if (typeof document === 'undefined') return

      const isCookieConsentVisible = isCookieConsentDialogVisible()
      const isShowingUpdatedLegalDocsModal = Boolean(document.getElementById(UPDATED_LEGAL_DOCS_MODAL_ID))
      const hasDismissed = getBooleanFromSessionStorage('getAppBannerDismissed', false)

      if (isCookieConsentVisible || isShowingUpdatedLegalDocsModal || hasDismissed) return
      setBannerVisible(true)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [getFeatureValue, deepLink])

  const value = useMemo(() => {
    return { bannerVisible, deepLink, isLoading: false }
  }, [bannerVisible, deepLink])

  return (
    <GetAppBannerContext.Provider value={value}>
      {children}

      <AppPromoModal
        analytics={{
          context,
          projectSlug,
        }}
        isOpen={bannerVisible}
        onClose={() => {
          writeToSessionStorage('getAppBannerDismissed', true)
          setBannerVisible(false)
        }}
      />
    </GetAppBannerContext.Provider>
  )
}

export const useGetAppBanner = (): GetAppBannerContext => {
  return useContext(GetAppBannerContext)
}
